import { createContext, useCallback, useContext, useRef, useState } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined);

/**
 * confirm() の代わりに使う確認ダイアログ。
 * `const ok = await confirm({ title, message, danger: true })` のように
 * 戻り値（Promise<boolean>）でユーザーの選択を受け取れる。
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    setState(options);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleClose = useCallback((result: boolean) => {
    setState(null);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state && (
        <Modal
          open
          onClose={() => handleClose(false)}
          maxWidth="max-w-sm"
          zIndexClassName="z-[110]"
          footer={
            <>
              <Button variant="outline" onClick={() => handleClose(false)}>
                {state.cancelLabel ?? 'キャンセル'}
              </Button>
              <Button
                variant={state.danger ? 'danger' : 'primary'}
                onClick={() => handleClose(true)}
              >
                {state.confirmLabel ?? 'OK'}
              </Button>
            </>
          }
        >
          {state.title && (
            <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
              {state.danger && (
                <i className="ri-error-warning-line text-red-500"></i>
              )}
              {state.title}
            </h3>
          )}
          <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
            {state.message}
          </p>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return ctx;
}